/* Dink Society — add-to-cart confirmation panel.
 * Listens to the theme's own `cart:update` event (fired by product-form-component
 * only after /cart/add.js succeeds), then reads /cart.js so the panel shows
 * exactly what Shopify put in the cart. No add-to-cart request is made here. */
(() => {
  if (customElements.get('ds-atc-popup')) return;

  const PENDING_MS = 10000;

  /* Block a second submit of the same product form while its add request is in flight. */
  const pending = new WeakMap();
  const release = (el) => {
    const t = pending.get(el);
    if (t) clearTimeout(t);
    pending.delete(el);
  };
  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const component = form instanceof HTMLFormElement ? form.closest('product-form-component') : null;
      if (!component) return;
      if (pending.has(component)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const id = form.querySelector('input[name="id"]')?.value;
      if (!id) return; // size not chosen: the theme shows its own prompt, nothing is sent
      pending.set(component, setTimeout(() => pending.delete(component), PENDING_MS));
    },
    true
  );
  /* This panel replaces the cart drawer's auto-open after a product-form add, so the two
   * never compete. Runs in the capture phase, before the drawer's own listener; the drawer
   * still opens normally from the cart icon. (Header re-renders restore the attribute,
   * so it is cleared at each add rather than once.) */
  window.addEventListener(
    'cart:update',
    (e) => {
      if (e.detail?.data?.source !== 'product-form-component') return;
      document.querySelectorAll('cart-drawer-component[auto-open]').forEach((d) => d.removeAttribute('auto-open'));
    },
    true
  );

  document.addEventListener('cart:error', (e) => {
    const c = e.target instanceof Element ? e.target.closest('product-form-component') : null;
    if (c) release(c);
  });

  const imgUrl = (src, width) => {
    if (!src) return '';
    try {
      const u = new URL(src, location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (_) {
      return src;
    }
  };

  class DsAtcPopup extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      const { signal } = this.ac;
      this.img = this.querySelector('[data-ds-atc-img]');
      this.titleEl = this.querySelector('[data-ds-atc-title]');
      this.opts = this.querySelector('[data-ds-atc-opts]');
      this.count = this.querySelector('[data-ds-atc-count]');
      this.live = this.querySelector('[data-ds-atc-live]');
      this.heading = this.querySelector('#DsAtcHeading');

      document.addEventListener('cart:update', (e) => this.onCartUpdate(e), { signal });
      this.querySelectorAll('[data-ds-atc-close]').forEach((b) =>
        b.addEventListener('click', () => this.close(true), { signal })
      );
      document.addEventListener(
        'keydown',
        (e) => {
          if (e.key === 'Escape' && this.isOpen) this.close(true);
        },
        { signal }
      );
      document.addEventListener(
        'pointerdown',
        (e) => {
          if (!this.isOpen || this.contains(e.target)) return;
          // Leave the panel alone when the shopper is pressing an add button again.
          if (e.target instanceof Element && e.target.closest('product-form-component, dink-sticky-cart')) return;
          this.close(false);
        },
        { signal }
      );
      window.addEventListener('resize', () => this.isOpen && this.position(), { signal, passive: true });
      window.addEventListener('scroll', () => this.isOpen && this.position(), { signal, passive: true });
      this.requestId = 0;
    }

    disconnectedCallback() {
      this.ac?.abort();
    }

    get isOpen() {
      return !this.hidden;
    }

    async onCartUpdate(event) {
      const data = event.detail?.data || {};
      if (data.source !== 'product-form-component') return;
      const component = event.target instanceof Element ? event.target.closest('product-form-component') : null;
      if (component) release(component);

      const reqId = ++this.requestId;
      let cart;
      try {
        const res = await fetch(`${(window.Theme?.routes?.cart_url || '/cart')}.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (!res.ok) throw new Error(`cart.js ${res.status}`);
        cart = await res.json();
      } catch (err) {
        console.warn('[ds-atc-popup] could not read cart', err);
        return;
      }
      if (reqId !== this.requestId) return;

      // Put the header bubble on Shopify's real count.
      document.querySelectorAll('cart-icon').forEach((icon) => {
        if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(cart.item_count, false, false);
      });

      if (data.didError) return; // never show the panel when the add failed

      const variantId = String(event.detail?.sourceId || '');
      const item = (cart.items || []).find((i) => String(i.variant_id) === variantId);
      if (!item) return;
      this.render(item, cart.item_count, Number(data.itemCount) || 1);
      this.open();
    }

    render(item, cartCount, addedQty) {
      const src = item.featured_image?.url || item.image;
      if (src) {
        this.img.src = imgUrl(src, 200);
        this.img.alt = item.featured_image?.alt || item.product_title || '';
        this.img.hidden = false;
      } else {
        this.img.hidden = true;
      }
      this.titleEl.textContent = item.product_title || item.title || '';

      this.opts.replaceChildren();
      const rows = [];
      (item.options_with_values || []).forEach((o) => {
        if (!o || o.name === 'Title' || o.value === 'Default Title') return;
        rows.push([o.name, o.value]);
      });
      if (addedQty > 1) rows.push(['Qty', String(addedQty)]);
      rows.forEach(([name, value]) => {
        const row = document.createElement('div');
        const dt = document.createElement('dt');
        const dd = document.createElement('dd');
        dt.textContent = `${name}:`;
        dd.textContent = value;
        row.append(dt, dd);
        this.opts.append(row);
      });

      this.count.textContent = String(cartCount);
      this.live.textContent = `${item.product_title} added to your cart. Cart has ${cartCount} item${cartCount === 1 ? '' : 's'}.`;
    }

    position() {
      // Anchor under the visible header cart icon; fall back to the top-right corner.
      const icon = [...document.querySelectorAll('.header-actions__cart-icon')]
        .map((el) => el.getBoundingClientRect())
        .find((r) => r.width > 0 && r.bottom > 0 && r.top < innerHeight);
      let top = 12;
      let right = 16;
      if (icon) {
        top = Math.round(icon.bottom) + 10;
        right = Math.max(12, Math.round(document.documentElement.clientWidth - icon.right) - 4);
      }
      this.style.setProperty('--ds-atc-top', `${top}px`);
      this.style.setProperty('--ds-atc-right', `${right}px`);
    }

    open() {
      const active = document.activeElement;
      if (!this.isOpen) this.returnFocus = active instanceof HTMLElement && active !== document.body ? active : null;
      this.position();
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));
      this.heading?.focus({ preventScroll: true });
      requestAnimationFrame(() => this.uncoverAddButton());
    }

    /* Phones: if the panel lands on top of the main Add to cart button, scroll the page
     * a little so the button sits just below the panel. Desktop is never moved. */
    uncoverAddButton() {
      if (!window.matchMedia('(max-width: 749px)').matches || !this.isOpen) return;
      const btn = document.querySelector('product-form-component [ref="addToCartButton"]');
      if (!btn) return;
      const b = btn.getBoundingClientRect();
      const p = this.getBoundingClientRect();
      if (!b.height || b.bottom <= p.top || b.top >= p.bottom) return;
      const shift = Math.ceil(p.bottom - b.top) + 12;
      if (shift > 0 && shift < 240 && window.scrollY >= shift) {
        window.scrollBy({ top: -shift, behavior: 'smooth' });
      }
    }

    close(restoreFocus) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.hidden = true;
      this.live.textContent = '';
      if (restoreFocus && this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    }
  }

  customElements.define('ds-atc-popup', DsAtcPopup);
})();
